import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import useAppStore from '../store';
import { Upload, Download, AlertCircle, CheckCircle, Package, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AppVersion {
  id: string;
  version_number: string;
  version_name: string | null;
  file_path: string;
  file_size: number | null;
  release_notes: string | null;
  uploaded_by: string;
  upload_date: string;
  is_active: boolean;
  download_count: number;
  created_at: string;
}

const AppVersionsPage: React.FC = () => {
  const { currentUser } = useAppStore();
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [versionNumber, setVersionNumber] = useState('');
  const [versionName, setVersionName] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [apkFile, setApkFile] = useState<File | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<AppVersion | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('app_versions')
        .select('*')
        .order('upload_date', { ascending: false });

      if (error) throw error;
      setVersions(data || []);
    } catch (err: any) {
      console.error('Error loading versions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.apk')) {
        setError('Veuillez sélectionner un fichier APK valide');
        return;
      }
      setApkFile(file);
      setError(null);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apkFile || !versionNumber || !currentUser) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const fileName = `${versionNumber}_${Date.now()}.apk`;
      const filePath = `apk-files/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('apk-files')
        .upload(filePath, apkFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from('app_versions')
        .insert({
          version_number: versionNumber,
          version_name: versionName || null,
          file_path: filePath,
          file_size: apkFile.size,
          release_notes: releaseNotes || null,
          uploaded_by: currentUser.id,
          is_active: true
        });

      if (insertError) {
        await supabase.storage.from('apk-files').remove([filePath]);
        throw insertError;
      }

      setSuccess('Version uploadée avec succès! Tous les utilisateurs ont été notifiés.');
      setVersionNumber('');
      setVersionName('');
      setReleaseNotes('');
      setApkFile(null);

      const fileInput = document.getElementById('apk-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      await loadVersions();
    } catch (err: any) {
      console.error('Error uploading APK:', err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const toggleVersionStatus = async (versionId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('app_versions')
        .update({ is_active: !currentStatus })
        .eq('id', versionId);

      if (error) throw error;

      setSuccess(`Version ${!currentStatus ? 'activée' : 'désactivée'} avec succès`);
      await loadVersions();
    } catch (err: any) {
      console.error('Error toggling version status:', err);
      setError(err.message);
    }
  };

  const openDeleteModal = (version: AppVersion) => {
    setVersionToDelete(version);
    setDeleteModalOpen(true);
    setError(null);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setVersionToDelete(null);
  };

  const handleDeleteVersion = async () => {
    if (!versionToDelete) return;

    try {
      setDeleting(true);
      setError(null);

      const { error: notificationError } = await supabase
        .from('app_notifications')
        .delete()
        .eq('version_id', versionToDelete.id);

      if (notificationError) throw notificationError;

      const { error: storageError } = await supabase.storage
        .from('apk-files')
        .remove([versionToDelete.file_path]);

      if (storageError) {
        console.warn('Storage deletion error (file may not exist):', storageError);
      }

      const { error: dbError } = await supabase
        .from('app_versions')
        .delete()
        .eq('id', versionToDelete.id);

      if (dbError) throw dbError;

      setSuccess('Version supprimée avec succès');
      closeDeleteModal();
      await loadVersions();
    } catch (err: any) {
      console.error('Error deleting version:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="text-center p-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-xl">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="h-8 w-8" />
          Gestion des versions APK
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Upload className="h-6 w-6" />
          Uploader une nouvelle version
        </h2>

        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de version *
              </label>
              <input
                type="text"
                value={versionNumber}
                onChange={(e) => setVersionNumber(e.target.value)}
                placeholder="Ex: 2.8.4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de la version
              </label>
              <input
                type="text"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder="Ex: Mise à jour majeure"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes de version (message facultatif)
            </label>
            <textarea
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="Décrivez les nouveautés de cette version..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fichier APK *
            </label>
            <input
              id="apk-file"
              type="file"
              accept=".apk"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {apkFile && (
              <p className="mt-2 text-sm text-gray-600">
                Fichier sélectionné: {apkFile.name} ({formatFileSize(apkFile.size)})
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={uploading}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Upload en cours...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                Uploader et notifier tous les utilisateurs
              </>
            )}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Versions existantes</h2>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : versions.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Aucune version disponible</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taille
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Téléchargements
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {versions.map((version, index) => {
                  const isOnlyVersion = versions.length <= 1;
                  return (
                    <tr key={version.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{version.version_number}</div>
                        {version.version_name && (
                          <div className="text-sm text-gray-500">{version.version_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {version.release_notes || 'Aucune note'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatFileSize(version.file_size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Download className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{version.download_count}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(version.upload_date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            version.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {version.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleVersionStatus(version.id, version.is_active)}
                            className={`px-3 py-1 rounded ${
                              version.is_active
                                ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                : 'bg-green-100 hover:bg-green-200 text-green-700'
                            }`}
                          >
                            {version.is_active ? 'Désactiver' : 'Activer'}
                          </button>
                          <button
                            onClick={() => openDeleteModal(version)}
                            disabled={isOnlyVersion}
                            className={`px-3 py-1 rounded flex items-center gap-1 ${
                              isOnlyVersion
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-red-100 hover:bg-red-200 text-red-700'
                            }`}
                            title={isOnlyVersion ? 'Au moins une version doit être conservée' : 'Supprimer cette version'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteModalOpen && versionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-red-500" />
                Confirmer la suppression
              </h3>
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer cette version ?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Version:</span>
                  <span className="text-gray-900">{versionToDelete.version_number}</span>
                </div>
                {versionToDelete.version_name && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Nom:</span>
                    <span className="text-gray-900">{versionToDelete.version_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Taille:</span>
                  <span className="text-gray-900">{formatFileSize(versionToDelete.file_size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Téléchargements:</span>
                  <span className="text-gray-900">{versionToDelete.download_count}</span>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Cette action est irréversible
                </p>
                <p className="text-red-700 text-sm mt-2">
                  Le fichier APK et toutes les données associées seront définitivement supprimés.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteVersion}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-5 w-5" />
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppVersionsPage;
